import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  ConsoleLine,
  FeedNote,
  GameMessage,
  HubTidings,
  InteractionWsPayload,
  NpcGiver,
  RoomStateObject,
  SceneSummary,
} from '@/hooks/types';
import { DEFAULT_NARRATIVE_RETENTION_LIMITS, narrativeBodyCache } from '@/game/narrativeRetention';
import type { MyRosterEntry } from '@/roster/types';
import type { CommandSpec } from '@/game/types';

export type RoomStateResyncStatus = 'idle' | 'pending' | 'success' | 'partial' | 'failure';

export type GameLifecycleState =
  | 'entry-idle'
  | 'entering'
  | 'entry-error'
  | 'ready-no-scene'
  | 'ready-scene'
  | 'reconnecting'
  | 'encounter'
  | 'aftermath';

interface RoomData {
  id: number;
  name: string;
  description: string;
  thumbnail_url: string | null;
  characters: RoomStateObject[];
  objects: RoomStateObject[];
  exits: RoomStateObject[];
  decorations?: string[];
  comfort_level?: number;
  is_owner: boolean;
  is_public: boolean;
  /** Civic-hub tidings block; null when no board/crier stands here (#1450). */
  hub: HubTidings | null;
  /** Active NPC placements standing in this room (#3044); absent on older fixtures. */
  npc_givers?: NpcGiver[];
  /** #3288 — true when ANY occupant is concealed. Identity-free OOC disclosure. */
  has_unseen_presence?: boolean;
  /** The viewer's own current Place, if any (#3810); sourced from the room_state push. */
  viewer_place_id: number | null;
}

/**
 * Exported (#2166) so selector-side derivations (e.g. `game/attention.ts`'s
 * `sessionAttention`) can type against a session without duplicating its shape.
 */
export interface Session {
  isConnected: boolean;
  /** Presentation state for entry/reconnect/exploration; server remains authoritative. */
  lifecycleState?: GameLifecycleState;
  messages: Array<GameMessage & { id: string }>;
  unread: number;
  commands: CommandSpec[];
  /**
   * Every name this character can type a command by, prefix-free and lowercased
   * (#4346): the `commands` frame's `names`. The composer reads it to tell `@desc`
   * (a command) from `@Nyx` (someone to address).
   */
  commandNames: string[];
  room: RoomData | null;
  scene: SceneSummary | null;
  sceneInteractions: InteractionWsPayload[];
  /** Bounded live-buffer state. A gap means the socket evicted rows that REST/history can recover. */
  sceneRetention?: { retained: number; evicted: number; warning: boolean; gap: boolean };
  /** Structured scene-less interactions for the current room, kept in memory only. */
  ambientInteractions?: InteractionWsPayload[];
  /** Connection diagnostics are kept separate from authored/system story text. */
  diagnostics?: string[];
  /**
   * Typed text lines (#3856): look results, item lines, errors, arrivals and
   * departures, narrative emits. Every `text` frame becomes one, kind from its
   * `kwargs.type`; both readers render them at their timestamp among the
   * interactions. Bounded, newest kept.
   */
  notes: FeedNote[];
  /** The staff console's lines (#3857): text frames tagged `console` by the server. Bounded. */
  consoleLines: ConsoleLine[];
  /**
   * Feed blocks this viewer folded to a one-line stub or removed from their own
   * view (#3856), keyed by `feedItemKey`. Per viewer, in memory only; nothing is
   * deleted for anyone else.
   */
  minimizedFeed: string[];
  dismissedFeed: string[];
  /** Epoch used to reject late interaction frames from the previous room. */
  ambientRoomEnteredAt?: number;
  /** Highest interaction id seen per thread key (#2156 per-thread unread badges). */
  threadLastSeen: Record<string, number>;
  /**
   * Highest interaction id present the moment the current scene's threading
   * was baselined (#2156 review fix). `countUnread` falls back to this scalar
   * for any thread key with no `threadLastSeen` entry, so a brand-new thread
   * that appears mid-session (e.g. a first whisper) badges unread starting
   * from its first message, while threads that already existed at scene load
   * stay zeroed. `null` before the baseline effect has run for this scene.
   */
  sceneBaselineId: number | null;
  /**
   * The conversation the rail has selected (#4129): a thread key, `room`, or a
   * page row's `page:<persona id>`; null is All, the whole feed. One at a
   * time; the composer's audience is derived from it every render.
   */
  activeThreadTab: string | null;
  /** Server revision of the latest accepted room snapshot. */
  stateEpoch?: string;
  stateSequence?: number;
  /** Character-scoped same-connection room-state recovery status. */
  roomStateResyncStatus?: RoomStateResyncStatus;
  /** Entry failure text shown in the no-room placeholder, not only as a toast. */
  entryError?: string;
  roomStateResyncError?: string;
  /**
   * Set when `puppet_changed` names this session's character after the
   * open-time puppet request (#3933) — confirms the handshake landed, rather
   * than just assuming it from the absence of a `command_error`.
   *
   * Its consumer is the #3934 connection recorder, which reads it as the
   * "puppet confirmed" readiness milestone in the entry timeline. Nothing
   * gates on it today: readiness stays `room_state`.
   */
  puppetConfirmed?: boolean;
}

interface GameState {
  sessions: Record<string, Session>;
  active: MyRosterEntry['name'] | null;
  /**
   * The durable server-side selection's RosterEntry id (#3412 state 2.5
   * substrate — `PlayerData.selected_entry_id`), mirrored alongside `active`
   * by `hydrateActiveCharacter`. `active` stays keyed by character NAME (a
   * standing rule for this slice, not revisited here — a rename touches ~25
   * call sites); this field is the seam a future refactor can use to key the
   * slice by id instead, without another full sweep.
   */
  activeEntryId: number | null;
  /**
   * This browser tab's own browsing identity (#3479) -- the `RosterEntry.id`
   * mirrored from `store/browsingIdentity.ts`'s `sessionStorage`-backed
   * per-tab store, once this tab has one. Distinct from `activeEntryId`:
   * that field is set by `hydrateActiveCharacter` alongside `active` and
   * still drives in-tab live-session focus (`GameWindow`'s tab strip,
   * `GatefoldPage`'s ADR-0247 redirect); `browsingEntryId` exists so ambient
   * (non-game) pages can read "who is this tab browsing as" without being
   * coupled to whichever character this tab's game surface happens to have
   * focused. `null` before this tab has been seeded (a hydration hasn't run
   * yet, or the account has no durable default selection).
   */
  browsingEntryId: number | null;
}

// Module-scope monotonic id for session messages (see addSessionMessage).
let nextMessageId = 0;
// Same for feed notes (#3856): several frames can land in one millisecond.
let nextNoteId = 0;
const MAX_NOTES = 200;
let nextConsoleLineId = 0;
const MAX_CONSOLE_LINES = 500;

const initialState: GameState = {
  sessions: {},
  active: null,
  activeEntryId: null,
  browsingEntryId: null,
};

type SessionRoomRevision = { epoch: string; sequence: number };

function isStaleRoomRevision(session: Session, revision?: SessionRoomRevision): boolean {
  return (
    (revision === undefined && session.stateEpoch !== undefined) ||
    (revision !== undefined &&
      session.stateEpoch === revision.epoch &&
      session.stateSequence !== undefined &&
      revision.sequence <= session.stateSequence)
  );
}

function applySessionRoomUpdate(
  session: Session,
  room: RoomData | null,
  revision: SessionRoomRevision | undefined,
  scene: SceneSummary | null | undefined
): void {
  if ((session.room?.id ?? null) !== (room?.id ?? null)) {
    if (session.ambientInteractions) session.ambientInteractions = [];
    session.ambientRoomEnteredAt = Date.now();
  }
  session.room = room;
  if (revision === undefined) return;
  session.stateEpoch = revision.epoch;
  session.stateSequence = revision.sequence;
  if (scene === undefined) return;
  if ((session.scene?.id ?? null) !== (scene?.id ?? null)) {
    session.sceneBaselineId = null;
    // The selection is scene-contextual (#4129): last scene's whisper set is a
    // mis-send vector. Only the room row survives a scene change.
    if (session.activeThreadTab !== 'room') session.activeThreadTab = null;
    session.sceneInteractions = [];
    session.sceneRetention = { retained: 0, evicted: 0, warning: false, gap: false };
  }
  session.scene = scene;
}

export const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    startSession: (state, action: PayloadAction<MyRosterEntry['name']>) => {
      const name = action.payload;
      if (!state.sessions[name]) {
        state.sessions[name] = {
          isConnected: false,
          messages: [],
          notes: [],
          consoleLines: [],
          minimizedFeed: [],
          dismissedFeed: [],
          unread: 0,
          commands: [],
          commandNames: [],
          room: null,
          scene: null,
          sceneInteractions: [],
          threadLastSeen: {},
          sceneBaselineId: null,
          activeThreadTab: null,
        };
      }
      state.active = name;
      state.sessions[name].unread = 0;
    },
    setActiveSession: (state, action: PayloadAction<MyRosterEntry['name']>) => {
      const name = action.payload;
      if (state.sessions[name]) {
        state.active = name;
        state.sessions[name].unread = 0;
      }
    },
    /**
     * Forget one character's session after its socket was closed on purpose
     * (#3818 "Leave the world"). `active` is left alone: it mirrors the durable
     * server selection (ADR-0241), and leaving the world does not un-select —
     * the player is still playing this character, offscreen.
     */
    endSession: (state, action: PayloadAction<MyRosterEntry['name']>) => {
      delete state.sessions[action.payload];
    },
    setSessionConnectionStatus: (
      state,
      action: PayloadAction<{ character: MyRosterEntry['name']; status: boolean }>
    ) => {
      const { character, status } = action.payload;
      const session = state.sessions[character];
      if (session) {
        session.isConnected = status;
        // A closed connection's handshake no longer holds (#3933): the next
        // open sends a fresh puppet request and must re-earn confirmation.
        if (!status) session.puppetConfirmed = undefined;
      }
    },
    /** `puppet_changed` named this session's own character (#3933): the open-time puppet landed. */
    setSessionPuppetConfirmed: (
      state,
      action: PayloadAction<{ character: MyRosterEntry['name'] }>
    ) => {
      const session = state.sessions[action.payload.character];
      if (session) session.puppetConfirmed = true;
    },
    setSessionLifecycle: (
      state,
      action: PayloadAction<{
        character: MyRosterEntry['name'];
        lifecycleState: GameLifecycleState;
      }>
    ) => {
      const session = state.sessions[action.payload.character];
      if (session) {
        session.lifecycleState = action.payload.lifecycleState;
        if (
          action.payload.lifecycleState === 'entering' ||
          action.payload.lifecycleState === 'ready-no-scene' ||
          action.payload.lifecycleState === 'ready-scene'
        ) {
          session.entryError = undefined;
        }
      }
    },
    setSessionEntryError: (
      state,
      action: PayloadAction<{ character: MyRosterEntry['name']; error: string }>
    ) => {
      const session = state.sessions[action.payload.character];
      if (session) session.entryError = action.payload.error;
    },
    resetSessionRoomRevision: (
      state,
      action: PayloadAction<{ character: MyRosterEntry['name'] }>
    ) => {
      const session = state.sessions[action.payload.character];
      if (session) {
        session.stateEpoch = undefined;
        session.stateSequence = undefined;
      }
    },
    setRoomStateResyncStatus: (
      state,
      action: PayloadAction<{
        character: MyRosterEntry['name'];
        status: RoomStateResyncStatus;
        error?: string;
      }>
    ) => {
      const session = state.sessions[action.payload.character];
      if (!session) return;
      session.roomStateResyncStatus = action.payload.status;
      session.roomStateResyncError = action.payload.error;
    },
    addSessionMessage: (
      state,
      action: PayloadAction<{ character: MyRosterEntry['name']; message: GameMessage }>
    ) => {
      const { character, message } = action.payload;
      const session = state.sessions[character];
      if (session) {
        // Monotonic counter, NOT Date.now() (2026-07 audit): multi-line server
        // output lands as several frames in the same millisecond, and the id
        // is used as a React key — duplicate keys dropped/misrendered rows.
        nextMessageId += 1;
        session.messages.push({ ...message, id: `m${nextMessageId}` });
        if (state.active !== character) {
          session.unread += 1;
        }
      }
    },
    clearSessionMessages: (state, action: PayloadAction<MyRosterEntry['name']>) => {
      const session = state.sessions[action.payload];
      if (session) {
        session.messages = [];
      }
    },
    setSessionCommands: (
      state,
      action: PayloadAction<{
        character: MyRosterEntry['name'];
        commands: CommandSpec[];
        commandNames: string[];
      }>
    ) => {
      const { character, commands, commandNames } = action.payload;
      const session = state.sessions[character];
      if (session) {
        session.commands = commands;
        session.commandNames = commandNames;
      }
    },
    setSessionRoom: (
      state,
      action: PayloadAction<{
        character: MyRosterEntry['name'];
        room: RoomData | null;
        revision?: SessionRoomRevision;
        scene?: SceneSummary | null;
      }>
    ) => {
      const { character, room, revision, scene } = action.payload;
      const session = state.sessions[character];
      if (!session || isStaleRoomRevision(session, revision)) return;
      applySessionRoomUpdate(session, room, revision, scene);
    },
    addAmbientInteraction: {
      prepare(payload: { character: MyRosterEntry['name']; interaction: InteractionWsPayload }) {
        narrativeBodyCache.put(payload.interaction);
        return { payload };
      },
      reducer: (
        state,
        action: PayloadAction<{
          character: MyRosterEntry['name'];
          interaction: InteractionWsPayload;
        }>
      ) => {
        const session = state.sessions[action.payload.character];
        if (!session) return;
        const frameTime = Date.parse(action.payload.interaction.timestamp);
        if (
          session.ambientRoomEnteredAt &&
          Number.isFinite(frameTime) &&
          frameTime < session.ambientRoomEnteredAt
        )
          return;
        const ambient = session.ambientInteractions ?? (session.ambientInteractions = []);
        if (ambient.some((item) => item.id === action.payload.interaction.id)) return;
        const { content: _content, line: _line, ...metadata } = action.payload.interaction;
        ambient.push(metadata as InteractionWsPayload);
        if (ambient.length > DEFAULT_NARRATIVE_RETENTION_LIMITS.maxTemporaryPoses) {
          session.ambientInteractions = ambient.slice(
            -DEFAULT_NARRATIVE_RETENTION_LIMITS.maxTemporaryPoses
          );
        }
      },
    },
    clearAmbientInteractions: (state, action: PayloadAction<MyRosterEntry['name']>) => {
      const session = state.sessions[action.payload];
      if (session) session.ambientInteractions = [];
    },
    minimizeFeedItem: (
      state,
      action: PayloadAction<{ character: MyRosterEntry['name']; key: string }>
    ) => {
      const session = state.sessions[action.payload.character];
      if (session && !session.minimizedFeed.includes(action.payload.key)) {
        session.minimizedFeed.push(action.payload.key);
      }
    },
    restoreFeedItem: (
      state,
      action: PayloadAction<{ character: MyRosterEntry['name']; key: string }>
    ) => {
      const session = state.sessions[action.payload.character];
      if (session) {
        session.minimizedFeed = session.minimizedFeed.filter((k) => k !== action.payload.key);
      }
    },
    dismissFeedItem: (
      state,
      action: PayloadAction<{ character: MyRosterEntry['name']; key: string }>
    ) => {
      const session = state.sessions[action.payload.character];
      if (!session) return;
      session.minimizedFeed = session.minimizedFeed.filter((k) => k !== action.payload.key);
      if (!session.dismissedFeed.includes(action.payload.key)) {
        session.dismissedFeed.push(action.payload.key);
      }
    },
    /** Fold several lines at once (#4128): one character's, or every line shown. */
    minimizeFeedItems: (
      state,
      action: PayloadAction<{ character: MyRosterEntry['name']; keys: string[] }>
    ) => {
      const session = state.sessions[action.payload.character];
      if (!session) return;
      for (const key of action.payload.keys) {
        if (!session.minimizedFeed.includes(key)) session.minimizedFeed.push(key);
      }
    },
    /** Hide several lines at once (#4128); a hidden line is no longer folded. */
    dismissFeedItems: (
      state,
      action: PayloadAction<{ character: MyRosterEntry['name']; keys: string[] }>
    ) => {
      const session = state.sessions[action.payload.character];
      if (!session) return;
      const keys = new Set(action.payload.keys);
      session.minimizedFeed = session.minimizedFeed.filter((k) => !keys.has(k));
      for (const key of keys) {
        if (!session.dismissedFeed.includes(key)) session.dismissedFeed.push(key);
      }
    },
    /** Expand all (#4128): unfold every line; hidden lines stay hidden. */
    restoreAllFeed: (state, action: PayloadAction<MyRosterEntry['name']>) => {
      const session = state.sessions[action.payload];
      if (session && session.minimizedFeed.length > 0) {
        session.minimizedFeed = [];
      }
    },
    restoreDismissedFeed: (state, action: PayloadAction<MyRosterEntry['name']>) => {
      const session = state.sessions[action.payload];
      if (session && session.dismissedFeed.length > 0) {
        session.dismissedFeed = [];
      }
    },
    addSessionDiagnostic: (
      state,
      action: PayloadAction<{ character: MyRosterEntry['name']; message: string }>
    ) => {
      const session = state.sessions[action.payload.character];
      if (!session) return;
      const diagnostics = session.diagnostics ?? (session.diagnostics = []);
      diagnostics.push(action.payload.message);
      if (diagnostics.length > 20) session.diagnostics = diagnostics.slice(-20);
    },
    clearSessionDiagnostics: (state, action: PayloadAction<MyRosterEntry['name']>) => {
      const session = state.sessions[action.payload];
      if (session) session.diagnostics = [];
    },
    /**
     * Append one typed text line (#3856). The id is assigned here, not by the
     * caller, for the same reason as `addSessionMessage`: it is a React key.
     * Unread counts the way a message does, so a look result or an error
     * arriving for a character you are not watching still badges that tab.
     */
    addFeedNote: (
      state,
      action: PayloadAction<{ character: MyRosterEntry['name']; note: Omit<FeedNote, 'id'> }>
    ) => {
      const { character, note } = action.payload;
      const session = state.sessions[character];
      if (!session) return;
      nextNoteId += 1;
      session.notes.push({ ...note, id: `n${nextNoteId}` });
      if (session.notes.length > MAX_NOTES) session.notes = session.notes.slice(-MAX_NOTES);
      if (state.active !== character) session.unread += 1;
    },
    clearFeedNotes: (state, action: PayloadAction<MyRosterEntry['name']>) => {
      const session = state.sessions[action.payload];
      if (session) session.notes = [];
    },
    /** A line the server tagged `console` (#3857): the staff console's, never the column's. */
    addConsoleLine: (
      state,
      action: PayloadAction<{
        character: MyRosterEntry['name'];
        content: string;
        sent?: boolean;
      }>
    ) => {
      const session = state.sessions[action.payload.character];
      if (!session) return;
      nextConsoleLineId += 1;
      session.consoleLines.push({
        id: `c${nextConsoleLineId}`,
        content: action.payload.content,
        ...(action.payload.sent ? { sent: true } : {}),
        timestamp: new Date().toISOString(),
      });
      if (session.consoleLines.length > MAX_CONSOLE_LINES) {
        session.consoleLines = session.consoleLines.slice(-MAX_CONSOLE_LINES);
      }
    },
    clearConsoleLines: (state, action: PayloadAction<MyRosterEntry['name']>) => {
      const session = state.sessions[action.payload];
      if (session) session.consoleLines = [];
    },
    setSessionScene: (
      state,
      action: PayloadAction<{
        character: MyRosterEntry['name'];
        scene: SceneSummary | null;
        revision?: { epoch: string; sequence: number };
      }>
    ) => {
      const { character, scene, revision } = action.payload;
      const session = state.sessions[character];
      if (session) {
        if (
          revision !== undefined &&
          session.stateEpoch === revision.epoch &&
          session.stateSequence !== undefined &&
          revision.sequence <= session.stateSequence
        ) {
          return;
        }
        // A scene change (including a brand-new scene at the same room)
        // invalidates any previous scene's baseline — the baseline effect
        // must re-run for the new scene id (#2156 review fix).
        const previousId = session.scene?.id ?? null;
        const nextId = scene?.id ?? null;
        if (previousId !== nextId) {
          session.sceneBaselineId = null;
          // The selection is scene-contextual (#4129): a stale row pointing at
          // last scene's table/whisper set is a mis-send vector. The room row
          // survives, since the room is still the room.
          if (session.activeThreadTab !== 'room') session.activeThreadTab = null;
          // The WS interaction buffer is scene-contextual too (2026-07 audit):
          // this clear used to be a separate unconditional dispatch on EVERY
          // room_state frame — and the backend broadcasts room_state to all
          // occupants whenever anything enters the room, so mid-scene arrivals
          // erased every pose/whisper buffered since the last REST fetch.
          // Clearing only on a real scene change keeps the feed intact.
          session.sceneInteractions = [];
          session.sceneRetention = { retained: 0, evicted: 0, warning: false, gap: false };
        }
        session.scene = scene;
        if (revision !== undefined) {
          session.stateEpoch = revision.epoch;
          session.stateSequence = revision.sequence;
        }
      }
    },
    addSceneInteraction: {
      prepare(payload: { character: MyRosterEntry['name']; interaction: InteractionWsPayload }) {
        // Direct dispatches (including replay fixtures) use the same boundary
        // as the socket handler, ensuring Redux never owns the body bytes.
        narrativeBodyCache.put(payload.interaction);
        return { payload };
      },
      reducer: (
        state,
        action: PayloadAction<{
          character: MyRosterEntry['name'];
          interaction: InteractionWsPayload;
        }>
      ) => {
        const { character, interaction } = action.payload;
        const session = state.sessions[character];
        if (session) {
          const MAX_TEMPORARY_POSES = DEFAULT_NARRATIVE_RETENTION_LIMITS.maxTemporaryPoses;
          if (session.sceneInteractions.some((item) => item.id === interaction.id)) return;
          // Keep only bounded identity/visibility metadata in Redux. The socket
          // handler stores the body in narrativeBodyCache before dispatching.
          const { content: _content, line: _line, ...metadata } = interaction;
          session.sceneInteractions.push(metadata as InteractionWsPayload);
          const previous = session.sceneRetention ?? {
            retained: 0,
            evicted: 0,
            warning: false,
            gap: false,
          };
          let evicted = previous.evicted;
          if (session.sceneInteractions.length > MAX_TEMPORARY_POSES) {
            const drop = session.sceneInteractions.length - MAX_TEMPORARY_POSES;
            session.sceneInteractions = session.sceneInteractions.slice(drop);
            evicted += drop;
          }
          session.sceneRetention = {
            retained: session.sceneInteractions.length,
            evicted,
            warning: session.sceneInteractions.length >= MAX_TEMPORARY_POSES * 0.8,
            gap: evicted > 0,
          };
        }
      },
    },
    // Deliberately does NOT touch `sceneBaselineId` (#2156 review fix 2): this
    // reducer is dispatched on every ROOM_STATE broadcast (handleRoomStatePayload),
    // which fires on ordinary room churn (anyone entering/leaving) — not just
    // scene changes. Nulling the baseline here made it stick at null for the
    // rest of the scene, since GamePage's one-shot baseline ref never re-fires
    // for the same scene id. The scene-CHANGE reset lives in `setSessionScene`
    // (guarded on an actual scene-id change), which pairs correctly with that ref.
    clearSceneInteractions: (state, action: PayloadAction<MyRosterEntry['name']>) => {
      const session = state.sessions[action.payload];
      if (session) {
        session.sceneInteractions = [];
        session.sceneRetention = { retained: 0, evicted: 0, warning: false, gap: false };
      }
    },
    // Scene-load baseline scalar (#2156 review fix): set once by GamePage's
    // baseline effect the first time it runs for a given scene id. See the
    // `sceneBaselineId` field doc for why this replaced the old per-thread-key
    // baseline (it one-shotted per KEY, so a brand-new thread's first message
    // got marked seen on arrival instead of badging unread).
    setSceneBaseline: (
      state,
      action: PayloadAction<{ character: MyRosterEntry['name']; baselineId: number | null }>
    ) => {
      const { character, baselineId } = action.payload;
      const session = state.sessions[character];
      if (session) {
        session.sceneBaselineId = baselineId;
      }
    },
    // Idempotent: never lowers an existing last-seen value for the thread key
    // (ratified unread semantics, #2156) — a stale/out-of-order dispatch must
    // not resurrect already-read interactions as unread.
    markThreadSeen: (
      state,
      action: PayloadAction<{
        character: MyRosterEntry['name'];
        threadKey: string;
        interactionId: number;
      }>
    ) => {
      const { character, threadKey, interactionId } = action.payload;
      const session = state.sessions[character];
      if (session) {
        const current = session.threadLastSeen[threadKey];
        if (current === undefined || interactionId > current) {
          session.threadLastSeen[threadKey] = interactionId;
        }
      }
    },
    // The rail's selection (#4129): any row's key, or null for All. Nothing
    // checks the key against a list of open conversations any more; the rail
    // offers only rows that exist, and a key whose thread has not backfilled
    // yet still derives a safe, fail-closed composer mode.
    setActiveThreadTab: (
      state,
      action: PayloadAction<{ character: MyRosterEntry['name']; threadKey: string | null }>
    ) => {
      const { character, threadKey } = action.payload;
      const session = state.sessions[character];
      if (session) {
        session.activeThreadTab = threadKey;
      }
    },
    resetGame: () => initialState,
    // Reload survival (#3412): mirrors the account query's `selected_entry`
    // into this slice on every successful fetch (see useAccountQuery) — a
    // truthy payload SETS active/activeEntryId, `null` CLEARS both (#3412
    // review fix: the explicit-clear path — mutate(null) + the account
    // refetch it triggers — must actually un-hydrate, not just leave the
    // slice stuck on the last-selected character forever). Deliberately does
    // NOT touch `sessions`: selection is not presence, so hydration/clearing
    // never starts OR tears down a session/connection. Concretely: clearing
    // can leave `active: null` while `sessions[thatName]` still holds a live,
    // connected session — the WebSocket (owned by useGameSocket, outside
    // Redux) keeps running and accumulating that session's state regardless;
    // only the "which character is currently highlighted/selected" pointer
    // moves. `active` can therefore point at a character with no
    // `sessions[active]` entry yet (a fresh SET with no session started), or
    // at nothing while a session persists orphaned in `sessions` (a CLEAR) —
    // callers (GameWindow, GameTopBar) must tolerate both.
    hydrateActiveCharacter: (
      state,
      action: PayloadAction<{ name: MyRosterEntry['name']; entryId: number } | null>
    ) => {
      if (action.payload === null) {
        state.active = null;
        state.activeEntryId = null;
        return;
      }
      const { name, entryId } = action.payload;
      state.active = name;
      state.activeEntryId = entryId;
    },
    // Per-tab browsing identity (#3479). Seeded (or reseeded) by
    // `useAccountQuery`'s hydration effect in `evennia_replacements/queries.tsx`
    // only when this tab's `sessionStorage` store is empty or no longer owns
    // its stored entry -- never on every account refetch, which is what let
    // one tab's selection stomp another tab's `active` (see
    // `store/browsingIdentity.ts`'s doc comment and the #3479 ledger).
    setBrowsingIdentity: (state, action: PayloadAction<number>) => {
      state.browsingEntryId = action.payload;
    },
    clearBrowsingIdentity: (state) => {
      state.browsingEntryId = null;
    },
  },
});

export const {
  startSession,
  endSession,
  setActiveSession,
  setSessionConnectionStatus,
  setSessionPuppetConfirmed,
  setSessionLifecycle,
  setSessionEntryError,
  resetSessionRoomRevision,
  setRoomStateResyncStatus,
  addSessionMessage,
  clearSessionMessages,
  setSessionCommands,
  setSessionRoom,
  addAmbientInteraction,
  clearAmbientInteractions,
  addSessionDiagnostic,
  clearSessionDiagnostics,
  addFeedNote,
  clearFeedNotes,
  addConsoleLine,
  clearConsoleLines,
  minimizeFeedItem,
  restoreFeedItem,
  dismissFeedItem,
  minimizeFeedItems,
  dismissFeedItems,
  restoreAllFeed,
  restoreDismissedFeed,
  setSessionScene,
  addSceneInteraction,
  clearSceneInteractions,
  markThreadSeen,
  setSceneBaseline,
  setActiveThreadTab,
  resetGame,
  hydrateActiveCharacter,
  setBrowsingIdentity,
  clearBrowsingIdentity,
} = gameSlice.actions;

/**
 * This tab's browsing identity (#3479) -- see `GameState.browsingEntryId`'s
 * doc comment. Typed on a minimal shape (rather than importing `RootState`
 * from `./store`) to avoid a circular import between the slice and the
 * store that assembles it; `RootState`'s `game` field is structurally this
 * shape, so `useAppSelector(selectBrowsingEntryId)` type-checks against it.
 */
export const selectBrowsingEntryId = (state: { game: GameState }): number | null =>
  state.game.browsingEntryId;
